import type {
  CnmcWaitingPhase,
  CompanyOrder,
} from "../common/companyOrderTypes";
import { OrderStatus } from "../common/companyOrderTypes";

/**
 * What the Transactions view shows as an order's status: an open CNMC filing order's phase, else its
 * order status. The Status cell, its filter, the title-row counters and the CSV export all read it.
 */
export type OrderDisplayStatus = OrderStatus | CnmcWaitingPhase;

/** How a display status reads, as an i18n key and an optional pill class. */
interface DisplayStatusPresentation {
  key: string;
  pillClass?: string;
}

const DISPLAY_STATUSES: Record<OrderDisplayStatus, DisplayStatusPresentation> =
  {
    [OrderStatus.WAITING_CONFIRMATION]: {
      key: "company_orders.status.waiting_confirmation",
      pillClass: "pill-attention",
    },
    [OrderStatus.PENDING]: { key: "company_orders.status.pending" },
    [OrderStatus.PENDING_AGENT]: { key: "company_orders.status.pending_agent" },
    [OrderStatus.PROCESSING]: { key: "company_orders.status.processing" },
    [OrderStatus.COMPLETED]: { key: "company_orders.status.completed" },
    [OrderStatus.FAILED]: {
      key: "company_orders.status.failed",
      pillClass: "pill-attention",
    },
    [OrderStatus.CANCELLED]: {
      key: "company_orders.status.cancelled",
      pillClass: "pill-inactive",
    },
    SUBMIT: {
      key: "cnmc.outbound.order_status.SUBMIT",
      pillClass: "pill-attention",
    },
    WAIT_CNMC: { key: "cnmc.outbound.order_status.WAIT_CNMC" },
    WAIT_ACKNOWLEDGEMENTS: {
      key: "cnmc.outbound.order_status.WAIT_ACKNOWLEDGEMENTS",
    },
    WAIT_REGISTRY_SYNC: {
      key: "cnmc.outbound.order_status.WAIT_REGISTRY_SYNC",
    },
    CLOSE: {
      key: "cnmc.outbound.order_status.CLOSE",
      pillClass: "pill-attention",
    },
    CONTACT_SOLDERA: {
      key: "cnmc.outbound.order_status.CONTACT_SOLDERA",
      pillClass: "pill-attention",
    },
  };

/** Every display status, order statuses first, in the order the status filter lists them. */
export const DISPLAY_STATUS_VALUES = Object.keys(
  DISPLAY_STATUSES
) as OrderDisplayStatus[];

/** The server sends a phase only while the order waits on its filing, so the phase wins whenever present. */
export const displayStatus = (order: CompanyOrder): OrderDisplayStatus =>
  order.cnmc_phase ?? order.status;

export const displayStatusPresentation = (
  status: OrderDisplayStatus
): DisplayStatusPresentation => DISPLAY_STATUSES[status];

/** Whether the status is an open CNMC filing order's phase, which only users of the Spanish registry see. */
export const isCnmcPhase = (status: OrderDisplayStatus): boolean =>
  !(Object.values(OrderStatus) as string[]).includes(status);
